/**
 * 채팅방 페이지
 * - 실시간 메시징 (WebSocket + 폴링 폴백)
 * - 역할 뱃지 + 프로필 모달 (방장/부방장 관리)
 * - 멤버 캐시 자동 갱신
 * - 강제퇴장 감지 및 자동 이동
 */

import {
  apiGetMessages,
  apiSendMessage,
  apiGetRoom,
  apiChangeRole,
  apiKickMember,
  apiLeaveRoom,
  getToken,
} from '../lib/api';
import { encryptMessage, decryptMessage } from '../lib/crypto';
import { getRoomKey, saveRoomKey, getUserInfo } from '../lib/storage';
import { escapeHtml, formatTimeShort, getAvatarGradient } from '../lib/utils';
import { lockIcon } from '../lib/icons';
import { toast } from '../lib/toast';
import type { AppStateManager } from '../app';

function renderMarkdown(text: string): string {
  let enabled = false;
  try { const v = localStorage.getItem('kalpha_setting_markdownMode'); if (v) enabled = JSON.parse(v); } catch {}
  if (!enabled) return escapeHtml(text);
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<del>$1</del>');
}

interface ChatMessage {
  id: string; room_id: string; sender_id: string; sender_name: string;
  encrypted_content: string; iv: string; type: string; created_at: number;
}

let currentRoomId: string | null = null;
let currentRoomKey: string | null = null;
let ws: WebSocket | null = null;
let messagePollInterval: number | null = null;
let lastMessageId: string | null = null;
let memberCache: Record<string, { role: string; display_name: string; username: string }> = {};
let myRole = 'member';
const recentIds = new Set<string>();
/** 답장 상태 */
let replyTo: { id: string; sender: string; preview: string } | null = null;

let wsReconnectAttempts = 0;
let wsReconnectTimer: number | null = null;
const WS_MAX_RECONNECT = 10;
let _appRef: AppStateManager | null = null;
let isUserScrolledUp = false;
let pollTickCount = 0;
let _msgCountPending = 0;
let _msgCountTimer: number | null = null;

export function enterChatRoom(roomId: string, app: AppStateManager): void {
  currentRoomId = roomId;
  memberCache = {};
  myRole = 'member';
  recentIds.clear();
  wsReconnectAttempts = 0;
  isUserScrolledUp = false;
  _appRef = app;

  // 항상 서버에서 최신 room_key 가져오기 (로컬 캐시 무시)
  currentRoomKey = null;
  initRoom(roomId);
  setupMessageInput(app);
  setupBackButton(app);
  setupProfileClickDelegation();
  setupScrollTracking();
  setupNewMessageBadge();
  const listEl = document.getElementById('message-list');
  if (listEl) listEl.innerHTML = '<div class="flex items-center justify-center py-20"><div class="animate-spin rounded-full h-8 w-8 border-2 border-kalpha-400 border-t-transparent"></div></div>';
}

/** 방 초기화 (키 + 멤버 + 메시지) */
async function initRoom(roomId: string): Promise<void> {
  try {
    const res = await apiGetRoom(roomId);
    if (res.success && res.data) {
      const ui = getUserInfo();
      if (res.data.members) {
        for (const m of res.data.members) {
          memberCache[m.user_id] = { role: m.role || 'member', display_name: m.display_name || '', username: m.username || '' };
          if (m.user_id === ui?.user_id) myRole = m.role || 'member';
        }
      }
      if (res.data.room_key) {
        currentRoomKey = res.data.room_key;
        saveRoomKey(roomId, res.data.room_key);
      }
    }
  } catch (e) {
    console.error('방 초기화 오류:', e);
    toast('방 정보를 불러오지 못했습니다.', 'error');
    const listEl = document.getElementById('message-list');
    if (listEl) listEl.innerHTML = '<p class="text-center py-20 text-gray-400 text-sm">방 정보를 불러올 수 없습니다.</p>';
    return;
  }

  if (!currentRoomKey) {
    toast('암호화 키를 가져오지 못했습니다.', 'error');
    return;
  }

  loadMessages();
  await connectWebSocket(roomId);
  startPolling();
}

export function leaveChatRoom(): void {
  if (ws) { try { ws.close(); } catch {}; ws = null; }
  if (messagePollInterval) { clearInterval(messagePollInterval); messagePollInterval = null; }
  if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null; }
  lastMessageId = null; recentIds.clear();
  currentRoomId = null; currentRoomKey = null;
  _appRef = null;
  isUserScrolledUp = false;
  pollTickCount = 0;
  _msgCountPending = 0;
  if (_msgCountTimer) { clearTimeout(_msgCountTimer); _msgCountTimer = null; }
  hideNewMessageBadge();
  updateConnectionBadge('hidden');
  replyTo = null; hideReplyBar();
  const ind = document.getElementById('typing-indicator');
  if (ind) ind.innerHTML = '';
  const listEl = document.getElementById('message-list');
  if (listEl) { listEl.innerHTML = ''; listEl.removeEventListener('scroll', onMessageScroll); }
  // 정적 DOM 이벤트 리스너 제거
  const badge = document.getElementById('new-message-badge');
  if (badge && _newMessageBadgeHandler) badge.removeEventListener('click', _newMessageBadgeHandler);
  const input = document.getElementById('message-input') as HTMLTextAreaElement | null;
  const sendBtn = document.getElementById('btn-send') as HTMLButtonElement | null;
  if (input && _msgKeydownHandler) input.removeEventListener('keydown', _msgKeydownHandler);
  if (sendBtn && _msgClickHandler) sendBtn.removeEventListener('click', _msgClickHandler);
  if (input && _msgInputHandler) input.removeEventListener('input', _msgInputHandler);
}

/** 멤버 캐시 갱신 (주기적) */
async function refreshMemberCache(roomId: string): Promise<void> {
  try {
    const res = await apiGetRoom(roomId);
    if (res.success && res.data?.members) {
      const ui = getUserInfo();
      for (const m of res.data.members) {
        memberCache[m.user_id] = { role: m.role || 'member', display_name: m.display_name || '', username: m.username || '' };
        if (m.user_id === ui?.user_id) myRole = m.role || 'member';
      }
      if (!currentRoomKey && res.data.room_key) {
        currentRoomKey = res.data.room_key;
        saveRoomKey(roomId, res.data.room_key);
      }
    }
  } catch {}
}

/** kick 감지용 - 멤버 목록에 내가 없으면 퇴장된 것 */
function checkIfKicked(): boolean {
  const ui = getUserInfo();
  return !!(ui && !memberCache[ui.user_id] && currentRoomId);
}

/** 모든 메시지의 발신자 뱃지 다시 렌더링 */
function refreshMessageBadges(): void {
  const listEl = document.getElementById('message-list');
  if (!listEl) return;
  listEl.querySelectorAll('.user-sender').forEach((el) => {
    const userId = el.getAttribute('data-user-id')!;
    const member = memberCache[userId];
    const badgeHtml = member?.role === 'owner'
      ? '<span class="ml-1 text-[10px] badge-role badge-owner">방장</span>'
      : member?.role === 'sub_admin'
        ? '<span class="ml-1 text-[10px] badge-role badge-admin">부방장</span>'
        : '';
    // 기존 뱃지 제거 후 새로 추가
    el.querySelectorAll('span').forEach(s => s.remove());
    if (badgeHtml) el.insertAdjacentHTML('beforeend', badgeHtml);
  });
}

async function loadMessages(): Promise<void> {
  if (!currentRoomId) return;
  const listEl = document.getElementById('message-list');
  if (!listEl) return;
  listEl.innerHTML = '<div class="flex items-center justify-center py-20"><div class="animate-spin rounded-full h-8 w-8 border-2 border-kalpha-400 border-t-transparent"></div></div>';

  try {
    const res = await apiGetMessages(currentRoomId);
    if (res.success && res.data) {
      if (res.data.length > 0) lastMessageId = res.data[res.data.length - 1].id;
      // 메시지 통계 업데이트
      const prev = localStorage.getItem('kalpha_setting_msgCount');
      let count = prev ? parseInt(JSON.parse(prev)) || 0 : 0;
      if (res.data.length > count) { localStorage.setItem('kalpha_setting_msgCount', JSON.stringify(res.data.length)); }
      renderMessages(res.data);
      scrollToBottom();
    }
  } catch {
    listEl.innerHTML = '<p class="text-center py-20 text-gray-400 text-sm">메시지를 불러올 수 없습니다.</p>';
  }
}

async function renderMessages(messages: ChatMessage[]): Promise<void> {
  const listEl = document.getElementById('message-list');
  if (!listEl) return;
  const ui = getUserInfo();
  const myId = ui?.user_id;
  if (!currentRoomKey) { await new Promise(r => setTimeout(r, 200)); if (!currentRoomKey) return; }

  const rendered = await Promise.all(messages.map(async (msg) => {
    if (msg.id) recentIds.add(msg.id);
    let text: string; let locked = false;
    try { text = currentRoomKey ? await decryptMessage(msg.encrypted_content, msg.iv, currentRoomKey) : '암호화된 메시지'; } catch { text = '복호화 실패'; locked = true; }
    if (locked && !currentRoomKey) locked = true;
    return { ...msg, decryptedText: text, isMine: msg.sender_id === myId, locked: locked && !!currentRoomKey };
  }));

  listEl.innerHTML = rendered.map((msg, i) => {
    if (msg.type === 'system') return `<div class="flex justify-center my-3"><span class="text-[11px] text-gray-400 bg-gray-100/80 px-3 py-1 rounded-full">${escapeHtml(msg.decryptedText)}</span></div>`;
    const prev = i > 0 ? rendered[i - 1] : null;
    const showSender = !prev || prev.sender_id !== msg.sender_id;
    const member = memberCache[msg.sender_id];
    const badge = member?.role === 'owner' ? '<span class="ml-1 text-[10px] badge-role badge-owner">방장</span>' : member?.role === 'sub_admin' ? '<span class="ml-1 text-[10px] badge-role badge-admin">부방장</span>' : '';
    return `<div class="flex ${msg.isMine ? 'justify-end' : 'justify-start'} mb-1 ${msg.isMine ? 'msg-enter-mine' : 'msg-enter-other'}">
      <div class="max-w-[78%] ${msg.isMine ? 'order-2' : 'order-1'}">
        ${showSender && !msg.isMine ? `<p class="text-[11px] text-gray-500 font-medium mb-0.5 ml-1 cursor-pointer hover:text-kalpha-600 transition-colors user-sender" data-user-id="${msg.sender_id}">${escapeHtml(msg.sender_name)}${badge}</p>` : ''}
        <div class="flex items-end gap-1 ${msg.isMine ? 'flex-row-reverse' : ''}">
          <div class="px-3.5 py-2 rounded-2xl text-[14px] leading-relaxed break-words shadow-sm msg-bubble-clickable ${msg.isMine       ? 'msg-bubble-mine text-white rounded-br-sm'
      : msg.locked ? 'bg-white text-gray-400 italic rounded-bl-sm border border-gray-100' : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100'}" data-msg-id="${msg.id}" data-msg-sender="${escapeHtml(msg.sender_name)}" data-msg-preview="${escapeHtml(msg.decryptedText || '')}">
            ${msg.locked ? `<span class="inline-flex items-center gap-1">${lockIcon(12)}${escapeHtml(msg.decryptedText)}</span>` : renderMarkdown(msg.decryptedText)}
            ${(msg as any).reply_preview ? `<div class="mt-1.5 pt-1.5 border-t border-white/20 text-[11px] opacity-75 flex items-center gap-1"><svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg><span class="font-medium">${escapeHtml((msg as any).reply_sender || '')}:</span> ${escapeHtml((msg as any).reply_preview || '')}</div>` : ''}
          </div>
          <span class="text-[10px] text-gray-400 shrink-0 mb-0.5">${formatTimeShort(msg.created_at)}</span>
        </div>
      </div>
    </div>`;
  }).join('');
}

function scrollToBottom(): void {
  requestAnimationFrame(() => {
    const el = document.getElementById('message-list');
    if (el) { el.scrollTop = el.scrollHeight; isUserScrolledUp = false; hideNewMessageBadge(); }
  });
}

function isNearBottom(): boolean {
  const el = document.getElementById('message-list');
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
}

function setupScrollTracking(): void {
  const el = document.getElementById('message-list');
  if (!el) return;
  el.addEventListener('scroll', onMessageScroll, { passive: true });
}

function onMessageScroll(): void {
  const el = document.getElementById('message-list');
  if (!el) return;
  isUserScrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight >= 120;
  if (!isUserScrolledUp) hideNewMessageBadge();
}

function setupNewMessageBadge(): void {
  const badge = document.getElementById('new-message-badge');
  if (!badge) return;
  if (_newMessageBadgeHandler) badge.removeEventListener('click', _newMessageBadgeHandler);
  _newMessageBadgeHandler = () => { scrollToBottom(); hideNewMessageBadge(); };
  badge.addEventListener('click', _newMessageBadgeHandler);
}

function showNewMessageBadge(): void {
  const badge = document.getElementById('new-message-badge');
  if (badge) { badge.classList.remove('hidden'); badge.classList.add('animate-fade-in'); }
}

function hideNewMessageBadge(): void {
  const badge = document.getElementById('new-message-badge');
  if (badge) badge.classList.add('hidden');
}

/** WebSocket 연결 (티켓 기반 인증) */
async function connectWebSocket(roomId: string): Promise<void> {
  const ui = getUserInfo();
  if (!ui) return;
  if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null; }
  const token = getToken();
  if (!token) return;

  // 일회용 WebSocket 티켓 발급
  let ticket = '';
  try {
    const res = await fetch(`/api/rooms/${roomId}/ws-ticket`, { headers: { Authorization: `Bearer ${token}` } });
    const json = await res.json();
    if (json.success && json.data?.ticket) ticket = json.data.ticket;
  } catch { return; }
  if (!ticket) return;

  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${location.host}/api/rooms/${roomId}/ws?ticket=${encodeURIComponent(ticket)}&user_id=${ui.user_id}&username=${encodeURIComponent(ui.display_name)}`;
  try {
    ws = new WebSocket(wsUrl);
    ws.onopen = () => {
      wsReconnectAttempts = 0;
      updateConnectionBadge('online');
    };
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat' && currentRoomId === roomId && currentRoomKey) {
          appendNewMessage({ id: data.message_id, room_id: roomId, sender_id: data.sender_id, sender_name: data.sender_name, encrypted_content: data.encrypted_content, iv: data.iv, type: data.message_type || 'text', created_at: Math.floor(data.timestamp / 1000) });
        } else if (data.type === 'typing') {
          const ind = document.getElementById('typing-indicator');
          if (ind && data.sender_id !== ui.user_id) {
            ind.innerHTML = data.is_typing ? `<span class="inline-flex items-center gap-1 animate-fade-in">${data.sender_name} 입력 중<div class="flex gap-0.5"><span class="w-1 h-1 bg-gray-400 rounded-full typing-dot"></span><span class="w-1 h-1 bg-gray-400 rounded-full typing-dot"></span><span class="w-1 h-1 bg-gray-400 rounded-full typing-dot"></span></div></span>` : '';
          }
        } else if (data.type === 'system') {
          const joinNotify = localStorage.getItem('kalpha_setting_joinNotify');
          if (!joinNotify || JSON.parse(joinNotify)) appendSystemMessage(data.text);
        }
      } catch {}
    };
    const thisWs = ws;
    ws.onclose = () => {
      if (ws === thisWs) ws = null;
      updateConnectionBadge('offline');
      scheduleReconnect(roomId);
    };
    ws.onerror = () => {
      if (ws === thisWs) ws = null;
      updateConnectionBadge('error');
    };
  } catch {}
}

function scheduleReconnect(roomId: string): void {
  if (wsReconnectAttempts >= WS_MAX_RECONNECT) {
    updateConnectionBadge('disconnected');
    return;
  }
  wsReconnectAttempts++;
  const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts), 30000);
  wsReconnectTimer = window.setTimeout(() => {
    if (currentRoomId === roomId) connectWebSocket(roomId).catch(() => {});
  }, delay);
}

function updateConnectionBadge(state: 'online' | 'offline' | 'error' | 'disconnected' | 'hidden'): void {
  const badge = document.getElementById('connection-badge');
  if (!badge) return;
  if (state === 'hidden') { badge.classList.add('hidden'); return; }
  badge.classList.remove('hidden');
  const labels: Record<string, { text: string; cls: string }> = {
    online: { text: '실시간', cls: 'bg-kalpha-500/30 text-white' },
    offline: { text: '연결 중...', cls: 'bg-amber-500/30 text-amber-100' },
    error: { text: '연결 오류', cls: 'bg-red-500/30 text-red-100' },
    disconnected: { text: '오프라인', cls: 'bg-gray-500/30 text-gray-200' },
  };
  const cfg = labels[state] || labels.offline;
  badge.textContent = cfg.text;
  badge.className = `text-[10px] px-2 py-0.5 rounded-full font-medium tracking-tight transition-colors ${cfg.cls}`;
}

async function appendNewMessage(msg: ChatMessage): Promise<void> {
  if (!msg.id || recentIds.has(msg.id)) return;
  if (lastMessageId === msg.id) return;
  recentIds.add(msg.id);
  if (recentIds.size > 100) { const f = recentIds.values().next().value; if (f) recentIds.delete(f); }
  lastMessageId = msg.id;

  // 발신자 정보 memberCache에 없으면 추가 (이름만)
  if (!memberCache[msg.sender_id]) {
    memberCache[msg.sender_id] = { role: 'member', display_name: msg.sender_name, username: '' };
  }

  const listEl = document.getElementById('message-list');
  if (!listEl || !currentRoomKey) return;
  const ui = getUserInfo();
  const isMine = msg.sender_id === ui?.user_id;
  let text: string; let locked = false;
  try { text = await decryptMessage(msg.encrypted_content, msg.iv, currentRoomKey); } catch { text = '복호화 실패'; locked = true; }

  const member = memberCache[msg.sender_id];
  const badge = member?.role === 'owner' ? '<span class="ml-1 text-[10px] badge-role badge-owner">방장</span>' : member?.role === 'sub_admin' ? '<span class="ml-1 text-[10px] badge-role badge-admin">부방장</span>' : '';

  const html = `<div class="flex ${isMine ? 'justify-end' : 'justify-start'} mb-1 ${isMine ? 'msg-enter-mine' : 'msg-enter-other'}">
    <div class="max-w-[78%]">
      ${!isMine ? `<p class="text-[11px] text-gray-500 font-medium mb-0.5 ml-1 cursor-pointer hover:text-kalpha-600 transition-colors user-sender" data-user-id="${msg.sender_id}">${escapeHtml(msg.sender_name)}${badge}</p>` : ''}
      <div class="flex items-end gap-1 ${isMine ? 'flex-row-reverse' : ''}">
        <div class="px-3.5 py-2 rounded-2xl text-[14px] leading-relaxed break-words shadow-sm msg-bubble-clickable ${isMine ? 'msg-bubble-mine text-white rounded-br-sm' : locked ? 'msg-bubble-locked rounded-bl-sm border border-gray-100' : 'msg-bubble-other rounded-bl-sm border border-gray-100'}" data-msg-id="${msg.id}" data-msg-sender="${escapeHtml(msg.sender_name)}" data-msg-preview="${escapeHtml(text)}">
          ${locked ? `<span class="inline-flex items-center gap-1">${lockIcon(12)}${escapeHtml(text)}</span>` : escapeHtml(text)}
        </div>
        <span class="text-[10px] text-gray-400 shrink-0 mb-0.5">${formatTimeShort(msg.created_at)}</span>
      </div>
    </div>
  </div>`;
  listEl.insertAdjacentHTML('beforeend', html);
  if (isNearBottom()) scrollToBottom();
  else showNewMessageBadge();
  // 메시지 통계 증가 (디바운스)
  _msgCountPending++;
  if (_msgCountTimer) clearTimeout(_msgCountTimer);
  _msgCountTimer = window.setTimeout(() => {
    const prev = localStorage.getItem('kalpha_setting_msgCount');
    const cur = prev ? (parseInt(JSON.parse(prev)) || 0) + _msgCountPending : _msgCountPending;
    localStorage.setItem('kalpha_setting_msgCount', JSON.stringify(cur));
    _msgCountPending = 0;
    _msgCountTimer = null;
  }, 5000);
}

function appendSystemMessage(text: string): void {
  const listEl = document.getElementById('message-list');
  if (!listEl) return;
  listEl.insertAdjacentHTML('beforeend', `<div class="flex justify-center my-3 animate-fade-in"><span class="text-[11px] text-gray-400 bg-gray-100/80 px-3 py-1 rounded-full">${escapeHtml(text)}</span></div>`);
  scrollToBottom();
  // 강제퇴장 감지
  if (checkIfKicked()) {
    toast('채팅방에서 퇴장되었습니다.', 'error');
    setTimeout(() => { leaveChatRoom(); _appRef?.navigateTo('rooms'); }, 1500);
  }
}

/** 폴링 (WebSocket 없을 때만) */
function startPolling(): void {
  if (messagePollInterval) clearInterval(messagePollInterval);
  const pollSetting = localStorage.getItem('kalpha_setting_pollInterval');
  let interval = 3000;
  if (pollSetting) { try { const v = JSON.parse(pollSetting); interval = { '1s': 1000, '3s': 3000, '5s': 5000, '10s': 10000 }[v] || 3000; } catch {} }
  pollTickCount = 0;
  messagePollInterval = window.setInterval(async () => {
    if (!currentRoomId) return;
    // WebSocket 연결 중에는 폴링 스킵
    if (ws && ws.readyState === WebSocket.OPEN) return;
    try {
      const res = await apiGetMessages(currentRoomId, lastMessageId || undefined);
      if (res.success && res.data) {
        for (const msg of res.data) await appendNewMessage(msg);
      }
    } catch {}
    // 주기적으로 memberCache 갱신 (30초마다 한 번)
    pollTickCount++;
    const ticksFor30s = Math.max(1, Math.round(30000 / interval));
    if (currentRoomId && pollTickCount % ticksFor30s === 0) {
      refreshMemberCache(currentRoomId).then(refreshMessageBadges);
    }
  }, interval);
}

/** 메시지 입력 */
function setupMessageInput(_app: AppStateManager): void {
  const input = document.getElementById('message-input') as HTMLTextAreaElement;
  const sendBtn = document.getElementById('btn-send') as HTMLButtonElement;
  if (!input || !sendBtn) return;
  let sending = false;

  // 기존 리스너 제거 후 재등록
  if (_msgKeydownHandler) input.removeEventListener('keydown', _msgKeydownHandler);
  if (_msgClickHandler) sendBtn.removeEventListener('click', _msgClickHandler);
  if (_msgInputHandler) input.removeEventListener('input', _msgInputHandler);

  _msgKeydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!sending && !sendBtn.disabled) sendMsg(); }
  };
  _msgClickHandler = () => { if (!sending && !sendBtn.disabled) sendMsg(); };
  _msgInputHandler = () => {
    input.style.height = 'auto';
    const maxSetting = localStorage.getItem('kalpha_setting_inputMaxLines');
    let maxH = 96;
    if (maxSetting) { try { const v = JSON.parse(maxSetting); maxH = v === 'unlimited' ? 96 : parseInt(v) * 24; } catch {} }
    input.style.height = Math.min(input.scrollHeight, maxH) + 'px';
  };

  input.addEventListener('keydown', _msgKeydownHandler);
  sendBtn.addEventListener('click', _msgClickHandler);
  input.addEventListener('input', _msgInputHandler);

  async function sendMsg(): Promise<void> {
    const text = input.value.trim();
    if (!text || !currentRoomId) return;
    if (!currentRoomKey) { toast('암호화 키를 불러오는 중입니다.', 'info', 1500); return; }
    sending = true; sendBtn.disabled = true; sendBtn.classList.add('opacity-70');
    input.value = ''; input.style.height = 'auto';
    try {
      const { encryptedContent, iv } = await encryptMessage(text, currentRoomKey);
      const res = await apiSendMessage(currentRoomId, encryptedContent, iv, 'text', replyTo?.id, replyTo?.sender, replyTo?.preview);
      if (res.success && res.data) {
        replyTo = null;
        hideReplyBar();
        const ui = getUserInfo();
        if (ui) await appendNewMessage({ id: res.data.id, room_id: currentRoomId, sender_id: ui.user_id, sender_name: ui.display_name, encrypted_content: encryptedContent, iv, type: 'text', created_at: Math.floor(Date.now() / 1000) });
      } else input.value = text;
    } catch { input.value = text; }
    finally { sendBtn.disabled = false; sendBtn.classList.remove('opacity-70'); sending = false; input.focus(); }
  }
}

function setupBackButton(app: AppStateManager): void {
  const btn = document.getElementById('btn-back');
  if (btn) { btn.classList.remove('hidden'); btn.onclick = () => { leaveChatRoom(); app.navigateTo('rooms'); }; }
  const leaveBtn = document.getElementById('btn-leave');
  if (leaveBtn) {
    leaveBtn.classList.remove('hidden');
    leaveBtn.onclick = async () => {
      if (!currentRoomId) return;
      if (!confirm(myRole === 'owner' ? '방장이 나가면 채팅방이 삭제됩니다. 정말 나가시겠습니까?' : '채팅방을 나가시겠습니까?')) return;
      const res = await apiLeaveRoom(currentRoomId);
      if (res.success) { leaveChatRoom(); app.navigateTo('rooms'); }
      else toast('나가기 실패', 'error');
    };
  }
  const infoBtn = document.getElementById('btn-room-info');
  if (infoBtn) { infoBtn.classList.remove('hidden'); infoBtn.onclick = () => app.navigateTo('room-info', { roomId: currentRoomId || '', myRole }); }
  // 답장 취소 버튼
  document.getElementById('btn-cancel-reply')?.addEventListener('click', () => { replyTo = null; hideReplyBar(); });
}

function showReplyBar(id: string, sender: string, preview: string): void {
  replyTo = { id, sender, preview };
  document.getElementById('reply-bar')!.classList.remove('hidden');
  document.getElementById('reply-sender')!.textContent = sender;
  document.getElementById('reply-preview')!.textContent = preview || '암호화된 메시지';
}
function hideReplyBar(): void {
  document.getElementById('reply-bar')!.classList.add('hidden');
}

/** 프로필 클릭 위임 - enterChatRoom에서 호출 */
function setupProfileClickDelegation(): void {
  const listEl = document.getElementById('message-list');
  if (!listEl || listEl.dataset.listenerAttached) return;
  listEl.dataset.listenerAttached = '1';
  listEl.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('.user-sender') as HTMLElement;
    if (target) { const uid = target.getAttribute('data-user-id'); if (uid) showProfileModal(uid); return; }
  });
  setupMessageContextMenu(listEl);
}

let contextMenuEl: HTMLElement | null = null;
let activeBubble: HTMLElement | null = null;
let longPressTimer: number | null = null;
let isLongPress = false;
let _newMessageBadgeHandler: (() => void) | null = null;
let _msgKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
let _msgClickHandler: (() => void) | null = null;
let _msgInputHandler: (() => void) | null = null;

function getOrCreateContextMenu(): HTMLElement {
  if (contextMenuEl) return contextMenuEl;
  const menu = document.createElement('div');
  menu.id = 'msg-context-menu';
  menu.className = 'fixed z-[60] hidden rounded-xl shadow-xl surface-secondary border border-[var(--border-light)] py-1 min-w-[9rem] animate-fade-in-scale';
  menu.innerHTML = `
    <button type="button" class="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-tertiary transition-colors flex items-center gap-2" data-action="reply">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
      답장
    </button>
    <button type="button" class="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-tertiary transition-colors flex items-center gap-2" data-action="copy">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      복사
    </button>
  `;
  menu.querySelectorAll('button').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const action = (e.currentTarget as HTMLElement).getAttribute('data-action');
      handleContextMenuAction(action);
      hideContextMenu();
    });
  });
  document.body.appendChild(menu);
  contextMenuEl = menu;
  return menu;
}

function handleContextMenuAction(action: string | null): void {
  if (!activeBubble) return;
  const msgId = activeBubble.getAttribute('data-msg-id') || '';
  const sender = activeBubble.getAttribute('data-msg-sender') || '';
  const preview = (activeBubble.getAttribute('data-msg-preview') || '').substring(0, 60);
  if (action === 'reply' && msgId) {
    showReplyBar(msgId, sender, preview);
  } else if (action === 'copy') {
    const text = activeBubble.getAttribute('data-msg-preview') || '';
    if (text && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => toast('복사됨', 'success')).catch(() => toast('복사 실패', 'error'));
    } else {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast('복사됨', 'success'); } catch { toast('복사 실패', 'error'); }
      document.body.removeChild(ta);
    }
  }
  activeBubble = null;
}

function showContextMenu(x: number, y: number): void {
  const menu = getOrCreateContextMenu();
  menu.classList.remove('hidden');
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rect = menu.getBoundingClientRect();
  let left = x;
  let top = y;
  if (left + rect.width > vw - 8) left = vw - rect.width - 8;
  if (top + rect.height > vh - 8) top = vh - rect.height - 8;
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

function hideContextMenu(): void {
  contextMenuEl?.classList.add('hidden');
  activeBubble = null;
}

function setupMessageContextMenu(listEl: HTMLElement): void {
  // 우클릭 (데스크톱)
  listEl.addEventListener('contextmenu', (e) => {
    const bubble = (e.target as HTMLElement).closest('.msg-bubble-clickable') as HTMLElement;
    if (bubble) {
      e.preventDefault();
      activeBubble = bubble;
      showContextMenu(e.clientX, e.clientY);
    }
  });

  // Long-press (모바일)
  listEl.addEventListener('touchstart', (e) => {
    const bubble = (e.target as HTMLElement).closest('.msg-bubble-clickable') as HTMLElement;
    if (!bubble) return;
    isLongPress = false;
    activeBubble = bubble;
    longPressTimer = window.setTimeout(() => {
      isLongPress = true;
      const touch = (e.touches as any)[0];
      if (touch) showContextMenu(touch.clientX, touch.clientY);
    }, 600);
  }, { passive: true });

  listEl.addEventListener('touchend', () => {
    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
    if (isLongPress) { setTimeout(() => { if (!activeBubble) hideContextMenu(); }, 100); }
  });

  listEl.addEventListener('touchmove', () => {
    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
  }, { passive: true });

  // 외부 클릭 시 닫힘
  document.addEventListener('click', (e) => {
    if (contextMenuEl && !contextMenuEl.contains(e.target as Node)) hideContextMenu();
  });
}

/** 프로필 모달 */
function showProfileModal(userId: string): void {
  const member = memberCache[userId];
  if (!member) return;
  const overlay = document.getElementById('modal-overlay')!;
  const content = document.getElementById('modal-content')!;
  const ui = getUserInfo();
  const roleLabel: Record<string, string> = { owner: '방장', sub_admin: '부방장', member: '멤버' };
  const roleBadge: Record<string, string> = { owner: 'badge-role badge-owner', sub_admin: 'badge-role badge-admin', member: '' };

  // 방장/부방장 면책: owner는 누구에게도 제재 불가, sub_admin끼리 제재 불가
  const canKick = myRole === 'owner' || (myRole === 'sub_admin' && member.role === 'member');
  const canPromote = myRole === 'owner' && member.role === 'member';
  const canDemote = myRole === 'owner' && member.role === 'sub_admin';

  const gradient = getAvatarGradient(member.display_name || '');

  content.innerHTML = `
    <div class="text-center">
      <div class="w-16 h-16 ${gradient} rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg"><span class="text-white font-bold text-xl">${member.display_name.charAt(0).toUpperCase()}</span></div>
      <h3 class="text-lg font-bold text-gray-900">${escapeHtml(member.display_name)}</h3>
      <p class="text-sm text-gray-500">@${escapeHtml(member.username)}</p>
      <span class="inline-block mt-1.5 px-3 py-0.5 text-xs font-bold rounded-full ${member.role === 'owner' ? 'badge-role badge-owner' : member.role === 'sub_admin' ? 'badge-role badge-admin' : ''}" style="${member.role === 'member' ? 'color:var(--text-secondary);background:var(--bg-badge-member)' : ''}">${roleLabel[member.role]}</span>
    </div>
    <div class="mt-5 space-y-2">
      ${userId !== ui?.user_id && canKick ? `<button class="btn-kick w-full btn btn-danger py-3 text-sm flex items-center justify-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>채팅방에서 내보내기</button>` : ''}
      ${canPromote ? `<button class="btn-role w-full btn py-3 text-sm flex items-center justify-center gap-2" style="background:linear-gradient(135deg,#3b82f6,#2563eb);color:white;box-shadow:0 2px 8px rgba(59,130,246,0.25)" onmouseover="this.style.boxShadow='0 4px 14px rgba(59,130,246,0.35)'" onmouseout="this.style.boxShadow='0 2px 8px rgba(59,130,246,0.25)'" data-new-role="sub_admin"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>부방장 임명</button>` : ''}
      ${canDemote ? `<button class="btn-role w-full btn py-3 text-sm flex items-center justify-center gap-2" style="background:linear-gradient(135deg,#f59e0b,#d97706);color:white;box-shadow:0 2px 8px rgba(245,158,11,0.25)" onmouseover="this.style.boxShadow='0 4px 14px rgba(245,158,11,0.35)'" onmouseout="this.style.boxShadow='0 2px 8px rgba(245,158,11,0.25)'" data-new-role="member"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>부방장 해제</button>` : ''}
      <button id="btn-close-profile" class="w-full btn btn-ghost py-3 text-sm">닫기</button>
    </div>`;
  showOverlay();

  content.querySelector('.btn-kick')?.addEventListener('click', async () => {
    if (!confirm('정말 내보내시겠습니까?')) return;
    const res = await apiKickMember(currentRoomId!, userId);
    if (res.success) {
      closeOverlay();
      delete memberCache[userId];
      appendSystemMessage(`${member.display_name}님이 퇴장되었습니다.`);
      refreshMessageBadges();
    } else toast('퇴장 실패', 'error');
  });

  content.querySelectorAll('.btn-role').forEach(btn => {
    btn.addEventListener('click', async function(this: HTMLElement) {
      const newRole = this.getAttribute('data-new-role')!;
      const res = await apiChangeRole(currentRoomId!, userId, newRole);
      if (res.success) {
        closeOverlay();
        // 캐시 즉시 갱신
        memberCache[userId] = { ...memberCache[userId], role: newRole };
        appendSystemMessage(`${member.display_name}님이 ${newRole === 'sub_admin' ? '부방장' : '멤버'}이(가) 되었습니다.`);
        refreshMessageBadges();
      } else toast('역할 변경 실패', 'error');
    });
  });

  document.getElementById('btn-close-profile')!.addEventListener('click', closeOverlay);
  if (!overlay.dataset.hasClickListener) {
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeOverlay(); });
    overlay.dataset.hasClickListener = '1';
  }
}

function showOverlay(): void {
  const o = document.getElementById('modal-overlay')!, c = document.getElementById('modal-content')!;
  o.classList.remove('hidden'); requestAnimationFrame(() => { o.classList.remove('opacity-0'); c.classList.remove('scale-95'); c.classList.add('scale-100'); });
}
function closeOverlay(): void {
  const o = document.getElementById('modal-overlay')!, c = document.getElementById('modal-content')!;
  o.classList.add('opacity-0'); c.classList.add('scale-95'); c.classList.remove('scale-100');
  setTimeout(() => o.classList.add('hidden'), 300);
}
